import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConnectionStore } from '@/stores/connectionStore';
import { SetupWizard } from '@/components/wizard';
import { Settings, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

export function SetupPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    isConnected,
    isLoading,
    connections,
    activeAccountId,
    setActiveAccount,
    checkConnection
  } = useConnectionStore();

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Handle query params from Zoom OAuth callback redirect
  useEffect(() => {
    const zoomResult = searchParams.get('oauth') || searchParams.get('zoom');
    if (zoomResult === 'success') {
      const account = searchParams.get('account');
      setSuccessMessage(
        account ? `Connected as ${account}` : 'Zoom account connected successfully'
      );
      // Clean URL params
      setSearchParams({}, { replace: true });
      // Refresh connection status
      checkConnection();
    } else if (zoomResult === 'error') {
      const message = searchParams.get('message') || 'Failed to connect Zoom account';
      setErrorMessage(message);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, checkConnection]);

  // Check connection status on mount
  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (isLoading) {
    return (
      <div className="flex h-screen flex-col bg-background text-foreground">
        <main className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </main>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="flex h-screen flex-col bg-background text-foreground">
        <main className="flex flex-1 items-center justify-center">
          <div className="text-center max-w-md">
            {/* Success banner */}
            {successMessage && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-900/20 p-3 text-green-200 text-sm justify-center">
                <CheckCircle className="h-4 w-4" />
                <span>{successMessage}</span>
              </div>
            )}

            {/* Error banner */}
            {errorMessage && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-900/20 p-3 text-red-200 text-sm justify-center">
                <AlertCircle className="h-4 w-4" />
                <span>{errorMessage}</span>
              </div>
            )}

            <Settings className="mx-auto h-12 w-12 text-muted-foreground" />
            <h2 className="mt-4 text-xl font-semibold">Connect Your Zoom Account</h2>
            <p className="mt-2 text-muted-foreground">
              Connect your Zoom account to use the Setup Wizard.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="mt-4 rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              Connect in Chat
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      {/* Success banner */}
      {successMessage && (
        <div className="flex items-center gap-2 bg-green-900/20 px-4 py-2 text-green-200 text-sm justify-center">
          <CheckCircle className="h-4 w-4" />
          <span>{successMessage}</span>
        </div>
      )}

      <SetupWizard
        accountId={activeAccountId ?? undefined}
        onAccountChange={setActiveAccount}
        connections={connections}
      />
    </div>
  );
}
